import { z } from "zod";
import { groupRoleSchema, groupMemberStatusSchema } from "./groups.js";
import { positiveMinorString } from "./ledger-correction.js";

/**
 * Shared DTO cho Remove Member — DELETE /v1/groups/:id/members/:memberId
 * (US-E4.F4-0027 / @UC0029 — Remove Member, Phase 3 RBAC `@gate`).
 *
 * 🔴 US-E2.F3-0870 (QĐ người dùng 30/09/2026) — `:memberId` = **`GroupMember.id`** (id SUẤT) cho MỌI trạng thái route nhận
 * (`active`, `unclaimed`). Trước đó route tra theo **userId** ⇒ `primaryUserId = X` trúng ghế đang giữ Ô CACHE của X — bố hai
 * con bấm xoá Bé Bo thì Bé Na bị xoá (Bé Bo không giữ cache; danh sách vẫn nêu userId của bố cho dòng Bé Bo). userId của app
 * CŨ còn được nhận làm đường DỰ PHÒNG, fail-closed: userId gắn ≥2 suất ⇒ 409 `MEMBER_ID_REQUIRED` (xem mã bên dưới).
 *
 * Một nguồn sự thật cho api (shape response + mã lỗi) và mobile (gọi + map lỗi), tránh drift (DRY).
 *
 * Cờ `GROUP_MEMBER_REMOVAL_ENABLED` MẶC ĐỊNH `true` từ QĐ PO 06/08/2026 (US-E24.F6-0355-S01 · @UC9580).
 *   Cờ chỉ còn là VAN KHẨN CẤP: đặt `=false` (rồi restart) ⇒ 409 REMOVAL_DISABLED, KHÔNG remove.
 *   Guards (self/owner/rbac/tenant) chạy đầy đủ ở mọi giá trị cờ.
 *
 * Remove = set GroupMember.status='removed' (SOFT-delete, preserve history — KHÔNG hard-delete, KHÔNG
 * xoá lịch sử giao dịch). AC2 debt-check THẬT: `FinancialPositionService.hasNonZeroPosition` trong tx
 * write ⇒ còn vị thế ⇒ 409 `FINANCIAL_POSITION_NONZERO` (KHÔNG bịa số nợ). AC1 push notif: CHƯA có.
 */

/**
 * Mã lỗi máy-đọc-được (RFC7807 problem+json `code`) cho remove-member — ổn định qua i18n/đổi wording.
 * Client discriminate theo `code`, KHÔNG so khớp message.
 */
export const REMOVE_MEMBER_CODE = {
  /** Van khẩn cấp: cờ đặt tường minh `=false` (mặc định `true` từ QĐ PO 06/08) ⇒ chặn xoá thành viên. */
  REMOVAL_DISABLED: "GROUP_MEMBER_REMOVAL_DISABLED",
  /**
   * AC3: không thể tự xoá chính mình. 🔴 US-E2.F3-0870 — "chính mình" = suất mà actor là cột cache `primaryUserId`
   * **HOẶC** actor đang đại diện (liên kết sống, không `viewer` — `userIsLinkedToSeat`). Chủ thể là SUẤT, không suy từ actor.
   */
  CANNOT_REMOVE_SELF: "GROUP_MEMBER_CANNOT_REMOVE_SELF",
  /** AC4: không thể xoá chủ nhóm (Owner). */
  CANNOT_REMOVE_OWNER: "GROUP_MEMBER_CANNOT_REMOVE_OWNER",
  /**
   * 🔴 US-E2.F3-0859 (@UC0028, QĐ người dùng 28/09/2026) — `?waiveUnpaidDues=true` được gửi cho một
   * target KHÔNG phải suất rác. Cờ đó chỉ dọn *"suất ở nhóm PHỤ HUYNH chưa từng có ai đại diện và
   * chưa từng vào nhóm"*; dùng nó ở chỗ khác là yêu cầu xoá **nợ thật** — kể cả suất giữ chỗ ở nhóm
   * THƯỜNG (người thật chưa cài app, review #1007 vòng 4 HIGH-1).
   *
   * Từ chối TƯỜNG MINH thay vì im lặng bỏ qua cờ: một cờ chạm tiền mà bị bỏ qua lặng lẽ thì người
   * gọi học được rằng "gửi cũng chẳng sao" — và một ngày nào đó nó sẽ KHÔNG bị bỏ qua.
   */
  WAIVE_NOT_APPLICABLE: "GROUP_MEMBER_WAIVE_NOT_APPLICABLE",
  /**
   * 🔴 US-E2.F3-0870 (@UC0029 · 409) — `:memberId` không phải id suất nào của nhóm, nên server thử đường DỰ PHÒNG của app
   * CŨ (coi nó là userId) — và userId đó GẮN ≥2 suất `active`/`unclaimed` trong nhóm (cột cache `primaryUserId` hoặc bất kỳ
   * liên kết sống nào, kể cả `viewer`). Không suy ra được suất nào ⇒ KHÔNG xoá gì. Đoán là đúng con bug này tồn tại để
   * diệt: bố hai con xoá Bé Bo ⇒ mất Bé Na; thủ quỹ nhận con ⇒ ghế thủ quỹ bị gỡ.
   */
  MEMBER_ID_REQUIRED: "GROUP_MEMBER_ID_REQUIRED",
  /**
   * 🔴 CONFLICT-CODE-NET-LOWS (@UC0029 @UC0055) — suất ĐÃ ở `removed` (một yêu cầu KHÁC vừa gỡ — compare-and-set đếm 0 dòng, đọc lại thấy
   * `removed`) ⇒ 409. Việc gỡ ĐÃ xong; tải lại sẽ thấy. Không `params`. Cũng là mã của người TỰ RỜI khi quản trị vừa gỡ suất của họ (M2
   * review #1200). Trước đây 409 chuỗi trần.
   */
  ALREADY_REMOVED: "GROUP_MEMBER_ALREADY_REMOVED",
  /**
   * 🔴 M1 review #1200 (@UC0029 @UC0055) — compare-and-set đếm 0 dòng mà suất KHÔNG bị gỡ cũng không phải ca riêng (vừa được nhận ⇒
   * `active`, vừa tự rời ⇒ `left`, đang chờ ⇒ `join_pending`…): trạng thái đổi bởi yêu cầu khác. `params.status` = trạng thái HIỆN TẠI
   * đọc lại (vắng khi không đọc được). Mã TRUNG TÍNH — không khẳng định "đã gỡ xong". Tải lại rồi quyết lại.
   */
  STATE_CHANGED: "GROUP_MEMBER_STATE_CHANGED",
} as const;
/** `detail` của `REMOVE_MEMBER_CODE.ALREADY_REMOVED` — giữ NGUYÊN câu cũ. */
export const MEMBER_ALREADY_REMOVED_MESSAGE = "Thành viên đã được xoá bởi một yêu cầu khác.";
/** `detail` của `REMOVE_MEMBER_CODE.ALREADY_REMOVED` ở cửa TỰ RỜI — người gọi chính là suất vừa bị gỡ. */
export const SELF_ALREADY_REMOVED_MESSAGE = "Bạn đã được gỡ khỏi nhóm bởi một yêu cầu khác.";
/** `detail` của `REMOVE_MEMBER_CODE.STATE_CHANGED` — câu trung tính (bản trước nói nhầm "đã được xoá" cho cả ca này). */
export const MEMBER_STATE_CHANGED_MESSAGE = "Trạng thái thành viên vừa thay đổi bởi một yêu cầu khác. Vui lòng tải lại.";

/**
 * Thông điệp lỗi (tiếng Việt sản phẩm) — một nguồn sự thật api ↔ mobile.
 * REMOVAL_DISABLED: wording fail-closed (chờ hệ thống công nợ Phase 7).
 */
export const REMOVAL_DISABLED_MESSAGE =
  "Chưa hỗ trợ xoá thành viên tới khi có hệ thống sổ cái (kiểm tra công nợ).";
export const CANNOT_REMOVE_SELF_MESSAGE = "Bạn không thể xoá chính mình hoặc người bạn đang đại diện khỏi nhóm.";
export const CANNOT_REMOVE_OWNER_MESSAGE = "Không thể xoá chủ nhóm.";
export const WAIVE_NOT_APPLICABLE_MESSAGE =
  "Chỉ dọn được suất ở nhóm phụ huynh chưa từng có người đại diện và chưa từng vào nhóm.";
export const MEMBER_ID_REQUIRED_MESSAGE =
  "Người này gắn với nhiều suất trong nhóm — hãy cập nhật ứng dụng rồi xoá từ đúng suất.";

/**
 * Shape response cho DELETE /v1/groups/:id/members/:memberId khi flag ON (remove thật thành công).
 *  - memberId: GroupMember.id đã chuyển status (soft-removed) — luôn là suất được CHỈ ĐỊNH (US-E2.F3-0870).
 *  - userId: cột cache `primaryUserId` của suất đó (xem field bên dưới).
 *  - status: trạng thái membership SAU remove — US-E4.F2-0396: nay là **`join_pending`** (HÀNG ĐỢI),
 *    không còn `removed`. Soft-delete vẫn giữ row; khác biệt là có ĐƯỜNG QUAY LẠI (O/A/T add lại
 *    qua `POST /groups/:id/members/add`) thay vì một ngõ cụt.
 *    🔴 Cố ý trả trạng thái THẬT ở đây — khác `selfLeaveResponseSchema` (giữ literal `"left"` vì nó
 *    nói với chính người vừa rời, "đang chờ duyệt" sẽ sai nghĩa với họ). Response này nói với
 *    ADMIN: admin cần biết người đó nằm trong hàng đợi thì mới biết là add lại được.
 * `.strict()` chặn rò field thừa (vd lịch sử giao dịch/secret) ra response (C2).
 *
 * ⚠️ Khi cờ bị tắt tường minh (`=false`) endpoint KHÔNG trả shape này — trả 409 REMOVAL_DISABLED.
 */
export const removeMemberResponseSchema = z
  .object({
    memberId: z.string().uuid(),
    /**
     * Cột cache `primaryUserId` của suất vừa xoá. 🔴 `null` khi suất không giữ cache: GHẾ CHƯA NHẬN (bug người dùng
     * 07/09/2026 — `primaryUserId = NULL`, `status='unclaimed'`) **và** suất `active` thứ hai của một người đại diện
     * (US-E2.F3-0870 — "Bé Bo": ô cache của bố đã nằm ở suất Bé Na). Trả chuỗi rỗng thay cho `null` là bịa một danh
     * tính; `.uuid()` không chấp nhận nó và cũng không nên chấp nhận.
     */
    userId: z.string().uuid().nullable(),
    status: groupMemberStatusSchema,
    role: groupRoleSchema,
  })
  .strict();

export type RemoveMemberResponse = z.infer<typeof removeMemberResponseSchema>;

/**
 * 🔴🔴 US-E2.F3-0859 (@UC0028 · `@gate money` · QĐ người dùng 28/09/2026) — query của
 * `DELETE /v1/groups/:id/members/:memberId`: **DỌN SUẤT RÁC**.
 *
 * VÌ SAO PHẢI CÓ: từ task này, huỷ/từ chối yêu cầu vào nhóm **không xoá suất** nữa — suất về
 * `unclaimed`. Nhưng `unclaimed` **nằm trong** `obligationMemberStatuses("parent")`, và worker
 * `collection-auto-enroll` chạy **mỗi 5 phút** sinh due cho mọi suất trong diện. Chỉ cần MỘT due
 * `pending` là `setRemovedInTx` trả 409 `FINANCIAL_POSITION_NONZERO` ⇒ một suất do người lạ tự gõ tên
 * tạo ra, sau khi bị từ chối, **KẸT VĨNH VIỄN** trong danh sách lớp và tổng phải-thu phồng theo.
 * Lối thoát *"O/A tự xoá tay"* mà `index.md §1` định giá chỉ tồn tại trong ~5 phút đầu.
 *
 * `hasNonZeroPosition` **không bị sửa một dòng** và vẫn chạy trong `setRemovedInTx`, CÙNG tx. Cờ đưa
 * due `pending`/`overdue` của suất sang `waived` TRƯỚC đó — đúng cách `DeceasedForgiveService` đã làm
 * (**0 bút toán**: due chưa từng post receivable nên tha nó = "không kỳ vọng thu nữa").
 *
 * 🔴 NHƯNG chốt cũ KHÔNG phải "lưới cuối" cho những gì cờ đã miễn: nó không đếm `waived`. Nó chỉ còn
 * chặn thứ cờ KHÔNG chạm (nợ ngang, khoản vay, due đã khai đóng / đóng một phần) — khi đó cả tx
 * rollback. Còn câu hỏi "suất này có phải rác không" thì các điều kiện của `waiveGhostSeatDuesInTx`
 * (`members.service.ts`) là phòng tuyến DUY NHẤT. Phần lớn điều kiện có ca đo riêng; vế
 * `primaryUserId: null` trong `where` CHƯA đo riêng được — lý do ghi ở docblock đó.
 *
 * Audit `group.member_due.ghost_seat_waived` ghi `waivedDueIds` + `waivedTotalsByCurrency` (tổng TÁCH theo
 * tiền tệ — không bao giờ cộng lẫn, §3.1).
 *
 * ⚠️ `z.enum(["true","false"])`, **KHÔNG** `z.coerce.boolean()`: query luôn là CHUỖI và
 * `Boolean("false") === true` ⇒ `?waiveUnpaidDues=false` sẽ **miễn nợ**, im lặng. Đây là lỗi đã có
 * tiền lệ review trong repo (`admin-billing` M1, `collection-progress`).
 *
 * `.strict()` ⇒ field lạ là **400 ở biên**: route này không bao giờ được nhận chủ thể hay số tiền từ
 * query.
 */
export const removeMemberQuerySchema = z
  .object({
    waiveUnpaidDues: z
      .enum(["true", "false"])
      .transform((v) => v === "true")
      .optional(),
  })
  .strict();

export type RemoveMemberQuery = z.infer<typeof removeMemberQuerySchema>;

/**
 * 🔴 US-E2.F3-0861 (@UC0029 · `@gate money`) — `params` của 409 `FINANCIAL_POSITION_NONZERO` khi target
 * là SUẤT DỌN ĐƯỢC bằng `?waiveUnpaidDues=true`. App đọc để hỏi người dùng *"còn N khoản (tổng X), xoá sẽ
 * miễn — tiếp tục?"* mà KHÔNG tự tính tiền (client hiển thị, server quyết định).
 *
 *  - `code` GIỮ NGUYÊN `FINANCIAL_POSITION_NONZERO` (tiền lệ `DELETE_ME_BLOCKED_REASON`, US-E19.F8-0858): app
 *    đã phát hành chỉ biết mã đó và vẫn hiện câu cũ.
 *  - Có mặt KHI VÀ CHỈ KHI dọn được (vị-từ dùng chung với đường miễn, `members.service.ts`
 *    `planGhostSeatWaiveInTx`). Không dọn được ⇒ **vắng hẳn** `params`, không phải `waivable: false`.
 *  - PHẲNG — `paramsFromException` (`problem-detail.filter.ts`) loại mọi object/array lồng.
 *  - Tiền = chuỗi BigInt minor > 0 + `currency` (§3.1). Khoản ở HAI loại tiền ⇒ không dọn được qua
 *    đường này (fail-closed, QĐ người dùng 29/09 Q3): một con số phẳng không nói được hai loại tiền.
 *  - `.strict()` — không tên suất, không id người (PII không đi đường lỗi).
 */
export const removeMemberWaivableParamsSchema = z
  .object({
    waivable: z.literal(true),
    /** Số khoản sẽ chuyển `waived` — đúng các khoản đường miễn sẽ lật. */
    waivableDueCount: z.number().int().min(1),
    /** Σ phần còn phải thu của đúng các khoản đó — SERVER cộng (BigInt), cùng phép cộng ghi vào audit. */
    waivableTotalMinor: positiveMinorString,
    currency: z.string().regex(/^[A-Z]{3}$/),
  })
  .strict();

export type RemoveMemberWaivableParams = z.infer<typeof removeMemberWaivableParamsSchema>;
